import type {
  SeriesApiError,
  SeriesBookVisit,
  SeriesCreateBody,
  SeriesPreviewBody,
  SeriesPreviewVisit,
  SeriesTemplate,
  SeriesUnit,
  SeriesWarning,
} from 'features/Series/api/series.types'
import { MAX_EVERY, MAX_NAME, MAX_SERIES_VISITS } from 'features/Series/series.constants'

import { intervalBetween, toUsDate, unitLabel } from '../seriesDates'
import { peopleKeys, samePeople, type VisitPeople } from '../seriesPeople'
import { SERIES_TEXT_V2 } from '../seriesText'
import type { GridEditPatch } from '../controls/SeriesGrid'
import type { GridRow, SeriesFooter } from '../series.types'
import { refusedVisitOf, visitErrorRef } from '../seriesErrors'

export type CreateHead = {
  templateId: number | null
  visits: number
  every: number
  unit: SeriesUnit
  name: string
  note: string
  issueId: number | null
}

export type CreateRow = {
  key: string
  date: string
  time: string
  duration: number
  // null follows the band's providers; a list was picked in the row.
  own: VisitPeople | null
  warnings: SeriesWarning[]
  added: boolean
  edited: { date: boolean; time: boolean }
  errors: string[]
}

export type CreateSeed = { date: string; time: string; duration: number }

// The cadence the rows on screen were built with.
export type CreateCadence = { every: number; unit: SeriesUnit }

const DEFAULT_DURATION = 15
const pad = (n: number, size = 2): string => String(n).padStart(size, '0')

// Reads the digits a dialog Date shows (site wall clock in browser-local time).
export const seedFromDialog = (
  date: Date,
  time: Date | null,
  duration: number | null,
): CreateSeed => ({
  date: `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
  time: time ? `${pad(time.getHours())}:${pad(time.getMinutes())}` : '',
  duration: duration && duration > 0 ? duration : DEFAULT_DURATION,
})

export type CreateBuilt = Pick<CreateHead, 'visits' | 'every' | 'unit'>

export const cadenceKey = (head: CreateBuilt): string => `${head.visits}|${head.every}|${head.unit}`

const within = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value))

export const headFromTemplate = (head: CreateHead, t: SeriesTemplate): CreateHead => ({
  ...head,
  templateId: t.id,
  visits: within(t.visits, 1, MAX_SERIES_VISITS),
  every: within(t.every, 1, MAX_EVERY),
  unit: t.unit,
  name: t.name,
})

export const rowsFromPreview = (visits: SeriesPreviewVisit[]): CreateRow[] =>
  visits.map((v) => ({
    key: `step-${v.step}`,
    date: v.date,
    time: v.time,
    duration: v.duration,
    own: null,
    warnings: v.warnings,
    added: false,
    edited: { date: false, time: false },
    errors: [],
  }))

// Appends preview[1] (the visit after the first) as a hand-added row.
export const appendAddedRow = (rows: CreateRow[], preview: SeriesPreviewVisit[]): CreateRow[] => {
  const next = preview[1]
  if (!next) return rows
  const top = Math.max(0, ...rows.map((r) => Number(/^added-(\d+)$/.exec(r.key)?.[1] ?? 0)))
  const [row] = rowsFromPreview([next])
  return [...rows, { ...row, key: `added-${top + 1}`, added: true }]
}

// A people-only pick keeps the preview's warnings; moving the visit drops them.
export const editCreateRow = (
  rows: CreateRow[],
  key: string,
  { date, time, duration, people }: GridEditPatch,
): CreateRow[] => {
  const moved = date !== undefined || time !== undefined || duration !== undefined
  return rows.map((r) =>
    r.key === key
      ? {
          ...r,
          date: date ?? r.date,
          time: time ?? r.time,
          duration: duration ?? r.duration,
          own: people ?? r.own,
          warnings: people && !moved ? r.warnings : [],
          errors: [],
          edited: {
            date: r.edited.date || date !== undefined,
            time: r.edited.time || time !== undefined,
          },
        }
      : r,
  )
}

export const removeCreateRow = (rows: CreateRow[], key: string): CreateRow[] =>
  rows.length <= 1 ? rows : rows.filter((r) => r.key !== key)

// Every row follows the band again.
export const applyBandToAll = (rows: CreateRow[]): CreateRow[] =>
  rows.map((r) => (r.own === null ? r : { ...r, own: null }))

// 1-based positions of the rows with their own providers.
export const ownSteps = (rows: CreateRow[]): number[] =>
  rows.flatMap((r, i) => (r.own !== null ? [i + 1] : []))

// The gap each row really has from the one before, so an unregenerated cadence never shows.
export const intervalLabels = (rows: CreateRow[], built: { unit: SeriesUnit }): string[] =>
  rows.map((r, i) => {
    if (r.added) return SERIES_TEXT_V2.intervalAdded
    if (i === 0) return SERIES_TEXT_V2.intervalStart
    return intervalBetween(rows[i - 1].date, r.date, built.unit) ?? SERIES_TEXT_V2.dash
  })

type BodyBase = { patientId: number; categoryId: number | null; head: CreateHead }

export const previewBodyOf = ({
  patientId,
  doctorId,
  categoryId,
  head,
  from,
  duration,
  visits,
}: BodyBase & {
  doctorId: number
  from: { date: string; time: string }
  duration: number
  visits: number
}): SeriesPreviewBody => ({
  patient_id: patientId,
  doctor_id: doctorId,
  ...(head.templateId !== null ? { template_id: head.templateId } : {}),
  date: from.date,
  time: from.time,
  visits,
  every: head.every,
  unit: head.unit,
  duration,
  ...(categoryId !== null ? { category_id: categoryId } : {}),
})

export const createBodyOf = ({
  patientId,
  band,
  categoryId,
  head,
  cadence,
  rows,
}: BodyBase & {
  band: VisitPeople
  cadence: CreateCadence
  rows: CreateRow[]
}): SeriesCreateBody => {
  const duration = rows[0]?.duration ?? DEFAULT_DURATION
  const note = head.note.trim()
  const visits: SeriesBookVisit[] = rows.map((r) => ({
    date: r.date,
    time: r.time,
    ...(r.duration !== duration ? { duration: r.duration } : {}),
    ...(r.own !== null && !samePeople(r.own, band) ? peopleKeys(r.own) : {}),
  }))
  return {
    patient_id: patientId,
    ...peopleKeys(band),
    ...(head.templateId !== null ? { template_id: head.templateId } : {}),
    name: head.name.trim(),
    ...(note ? { note } : {}),
    ...(categoryId !== null ? { category_id: categoryId } : {}),
    duration,
    every: cadence.every,
    unit: cadence.unit,
    issue_ids: head.issueId !== null ? [head.issueId] : [],
    visits,
  }
}

// Row index for each refused visit: a bare visits.N is step N (row N - 1).
const collectErrors = (error: SeriesApiError): Map<number, string[]> => {
  const found = new Map<number, string[]>()
  const add = (index: number, texts: string[]): void => {
    found.set(index, [...(found.get(index) ?? []), ...texts])
  }
  Object.entries(error.errors ?? {}).forEach(([key, texts]) => {
    const ref = visitErrorRef(key)
    if (ref) add(ref.kind === 'step' ? ref.n - 1 : ref.n, texts)
  })
  if (found.size === 0 && error.code === 'visit_refused') {
    const refused = refusedVisitOf(error.message)
    if (refused) add(refused.n - 1, [refused.text])
  }
  return found
}

export const markCreateErrors = (
  rows: CreateRow[],
  error: SeriesApiError,
): { rows: CreateRow[]; matched: boolean } => {
  const found = collectErrors(error)
  let matched = false
  const next = rows.map((r, i) => {
    const texts = found.get(i)
    if (!texts) return r
    matched = true
    return { ...r, errors: texts }
  })
  return { rows: next, matched }
}

const BAND_KEY = /^(doctor_id|doctor_ids(\.\d+)?|primary_doctor_id)$/

// Messages the backend filed under the band's people keys.
export const bandErrorsOf = (error: SeriesApiError): string[] =>
  Object.entries(error.errors ?? {}).flatMap(([key, texts]) => (BAND_KEY.test(key) ? texts : []))

export const canSaveCreate = ({
  head,
  rows,
  band,
  categoryId,
  isSaving,
  isPreviewing,
}: {
  head: CreateHead
  rows: CreateRow[]
  band: VisitPeople | null
  categoryId: number | null
  isSaving: boolean
  isPreviewing: boolean
}): boolean => {
  const name = head.name.trim()
  return (
    !isSaving &&
    !isPreviewing &&
    name.length >= 1 &&
    name.length <= MAX_NAME &&
    rows.length > 0 &&
    rows.every((r) => r.date !== '' && r.time !== '') &&
    categoryId !== null &&
    band !== null
  )
}

const needsAttention = (r: CreateRow): boolean =>
  r.errors.length > 0 || r.warnings.some((w) => w.refused)

export const createFooter = ({
  head,
  rows,
  stale,
  providerName,
  canSave,
}: {
  head: CreateHead
  rows: CreateRow[]
  stale: boolean
  providerName: string
  canSave: boolean
}): SeriesFooter => {
  const first = rows[0]
  const last = rows[rows.length - 1]
  const name = head.name.trim()
  // A blank name reads "New series" inside the full title (design Main.dc.html:930).
  const title =
    first && last
      ? SERIES_TEXT_V2.footerTitleCreate(
          name || SERIES_TEXT_V2.newSeries,
          rows.length,
          toUsDate(first.date),
          toUsDate(last.date),
        )
      : SERIES_TEXT_V2.newSeries
  const attention = rows.filter(needsAttention).length
  const base = SERIES_TEXT_V2.footerEvery(unitLabel(head.every, head.unit))
  const subtitle =
    base +
    (stale ? SERIES_TEXT_V2.footerStale : '') +
    (attention > 0 ? SERIES_TEXT_V2.footerAttention(attention) : '') +
    (providerName ? ` · ${providerName}` : '')
  return {
    kicker: SERIES_TEXT_V2.kickerSeries,
    title,
    subtitle,
    tone: attention > 0 ? 'warn' : 'muted',
    saveLabel: canSave ? SERIES_TEXT_V2.saveCreate(rows.length) : SERIES_TEXT_V2.save,
  }
}

// Rows of the shared visit grid; rows without their own people show the band's; all locked while saving.
export const createGridRows = (create: {
  rows: CreateRow[]
  intervals: string[]
  head: CreateHead
  band: VisitPeople | null
  isSaving?: boolean
}): GridRow[] =>
  create.rows.map((r, i) => ({
    key: r.key,
    visit: `${i + 1}/${create.rows.length}`,
    name: create.head.name.trim() || SERIES_TEXT_V2.noName,
    date: r.date || null,
    time: r.time || null,
    duration: r.duration,
    people: r.own ?? create.band,
    interval: create.intervals[i] ?? '',
    editable: true,
    disabled: Boolean(create.isSaving),
    edited: r.edited,
    warnings: [...r.warnings.map((w) => w.message), ...r.errors],
    refused: r.errors.length > 0 || r.warnings.some((w) => w.refused),
    removable: create.rows.length > 1 && !create.isSaving,
  }))
